import { useGSAP } from '@gsap/react';
import BlockContent from '@sanity/block-content-to-react';
import gsap from 'gsap';

import { graphql } from 'gatsby';
import React, { useRef } from 'react';
import { SanityImageType } from '../../../graphql-fragments/SanityImage';
import serializers from '../../../serializers';
import { BREAKPOINTS } from '../../../styles/breakpoints';
import { ModuleBgColor } from '../../../templates/Page';
import { RawPortableText } from '../../../types/types';
import { clsx } from '../../../utils/utils';
import { CommonModuleProps } from '../../ModulesContent';
import Image from '../Image';
import ModuleLayout from '../ModuleLayout';
import * as styles from './ImageWithBranchingInfoModule.module.scss';

export const ImageWithBranchingInfoModuleFragment = graphql`
  fragment ImageWithBranchingInfoModule on SanityPageImageWithBranchingInfoModule {
    title {
      ...LocaleStringFragment
    }
    text {
      ...LocaleTextFragment
    }
    mobileImage {
      ...SanityImage
    }
    image {
      ...SanityImage
    }
    infos {
      title {
        ...LocaleStringFragment
      }
      text {
        ...LocaleBoldPortableText
      }
    }
  }
`;

export type ImageWithBranchingInfoModuleProps = {
  title: string;
  text?: string;
  mobileImage?: SanityImageType;
  image: SanityImageType;
  infos: Array<{
    title?: string;
    text: RawPortableText;
  }>;

  className?: string;
};

export function getModuleBgColor(props: ImageWithBranchingInfoModuleProps): ModuleBgColor {
  /**
   * The purpose of this function is to let other modules know which background color this module has.
   * Knowing this, we can use this function to make decisions about the layout and spacing between modules.
   */
  return 'white';
}

function ImageWithBranchingInfoModule(
  props: ImageWithBranchingInfoModuleProps & CommonModuleProps,
): React.ReactElement {
  const { image, mobileImage, title, text, infos, className, previousModuleBgColor, moduleId } =
    props;

  const evenIndexArrayInfos = infos.filter((_, index) => index % 2 === 0);
  const oddIndexInfos = infos.filter((_, index) => index % 2 !== 0);

  function renderInfosContainer(
    infos: Array<{ title?: string; text: RawPortableText }>,
    isInTheLeftSide?: boolean,
  ) {
    return infos.map((info, i) => (
      <div className={clsx(styles.infoContainer, isInTheLeftSide && styles.leftBranch)} key={i}>
        <div className={styles.branchContainer}>
          <div className={styles.verticalContainer}>
            <div className={styles.dotContainer}>
              <div className={styles.dot}></div>
            </div>
            <div className={styles.lineContainer}>
              <div className={styles.branchLineVertical}></div>
              <div className={clsx(styles.branchLineHorizontal)}></div>
            </div>
          </div>
        </div>
        <div className={styles.infoTextContainer}>
          {info.title && <h4 className={styles.infoTitle}>{info.title}</h4>}
          <BlockContent
            blocks={info.text}
            renderContainerOnSingleChild
            serializers={serializers}
            className={styles.info}
          />
        </div>
      </div>
    ));
  }

  const containerRef = useRef<HTMLElement | null>(null);
  useGSAP(
    () => {
      const desktopLeftInfoContainers = gsap.utils.toArray<HTMLDivElement>(
        `.${styles.desktop} .${styles.infoContainer}.${styles.leftBranch}`,
      );

      const desktopRightInfoContainers = gsap.utils.toArray<HTMLDivElement>(
        `.${styles.desktop} .${styles.infoContainer}:not(.${styles.leftBranch})`,
      );

      const desktopInfoContainers: Array<HTMLDivElement> = [];
      for (let i = 0; i < desktopLeftInfoContainers.length; i++) {
        desktopInfoContainers.push(desktopLeftInfoContainers[i]);
        if (i < desktopRightInfoContainers.length) {
          desktopInfoContainers.push(desktopRightInfoContainers[i]);
        }
      }

      const mobileInfoContainers = gsap.utils.toArray<HTMLDivElement>(
        `.${styles.mobile} .${styles.infoContainer}`,
      );

      [desktopInfoContainers, mobileInfoContainers].forEach(infoContainers => {
        infoContainers.forEach((target, i) => {
          const isLeftSide = target.classList.contains(styles.leftBranch);

          const tl = gsap.timeline({
            delay: isLeftSide ? 0 : 0.5,
            scrollTrigger: {
              trigger: target,
              start: 'top 90%',
            } satisfies ScrollTrigger.Vars,
          });

          // entrance
          tl.from(target, {
            opacity: 0,
            y: -20,
            duration: 1,
          });

          gsap.context(() => {
            tl
              // animate vertical line
              .fromTo(
                '.' + styles.branchLineVertical,
                {
                  height: 0,
                },
                {
                  duration: 0.5,
                  height: '100%',
                },
                '<+=0.2',
              )
              // animate horizontal line
              .fromTo(
                `.${styles.branchLineHorizontal}`,
                {
                  width: 0,
                  ease: 'circ.out',
                  duration: 0.5,
                },
                {
                  width: isLeftSide ? 500 : 70,
                },
                '<+=0.5',
              );
          }, target);
        });
      });
    },
    { scope: containerRef },
  );

  return (
    <ModuleLayout
      id={moduleId}
      moduleRef={containerRef}
      className={clsx(className, styles.container)}
      contentClassName={styles.contentContainer}
      childrenClassName={styles.contentWrapper}
      childrenWithoutHorizontalPadding
      currentModuleBgColor={getModuleBgColor(props)}
      previousModuleBgColor={previousModuleBgColor}
    >
      <div className={styles.titleContainer}>
        {title && <h2 className={styles.title}>{title}</h2>}
        {text && <p className={styles.text}>{text}</p>}
      </div>
      <div className={styles.imageAndInfoContainer}>
        <div className={clsx(styles.infosContainer, styles.desktop)}>
          {renderInfosContainer(evenIndexArrayInfos, true)}
        </div>
        <div className={styles.imageWrapper}>
          <div className={styles.imageContainer}>
            <Image
              className={styles.image}
              image={mobileImage || image}
              dimensions={[
                [600, 2800],
                [BREAKPOINTS.tabletHorizontal, 800, 850],
                [BREAKPOINTS.smallLaptop, image, 1200, 2000],
              ]}
              cover
            />
          </div>
        </div>
        <div className={clsx(styles.infosContainer, styles.mobile)}>
          {renderInfosContainer(infos)}
        </div>
        <div className={clsx(styles.infosContainer, styles.desktop)}>
          {renderInfosContainer(oddIndexInfos)}
        </div>
      </div>
    </ModuleLayout>
  );
}

export default ImageWithBranchingInfoModule;
